import { formatAuthors, formatDate } from "@lib/format";
import { Badge } from "@components/components/ui/badge";

import type { AggregatedPaper } from "@types";
import { SummaryBlock } from "./SummaryBlock";

interface PaperCardProps {
  paper: AggregatedPaper;
  highlight: boolean;
}

export function PaperCard({ paper, highlight }: PaperCardProps) {
  const authors = formatAuthors(paper.authors);

  // 从 paper_url 中提取 arXiv ID
  const arxivId = paper.paper_id
    .replace("http://arxiv.org/abs/", "")
    .replace("v1", "")
    .replace("v2", "")
    .replace("v3", "")
    .replace("v4", "");
  const arxivAbsUrl = `https://arxiv.org/abs/${arxivId}`;
  const arxivPdfUrl = `https://arxiv.org/pdf/${arxivId}.pdf`;

  return (
    <article
      className={`group relative flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm transition-all hover:shadow-md sm:p-6 ${
        highlight ? "ring-1 ring-accent/50" : ""
      }`}
    >
      {highlight && (
        <div className="absolute right-6 top-6">
          <span className="flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent"></span>
          </span>
        </div>
      )}

      <header className="flex flex-col gap-2 pr-6">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-medium text-slate-700">{paper.topic}</span>
          <span>·</span>
          <span title={paper.publish_date}>{formatDate(paper.publish_date)}</span>
          {paper.primary_category && (
            <>
              <span>·</span>
              <Badge variant="secondary" className="text-[10px] font-medium uppercase tracking-wide">
                {paper.primary_category}
              </Badge>
            </>
          )}
        </div>

        <h2 className="text-lg font-bold leading-snug text-slate-900 transition group-hover:text-accent sm:text-xl">
          <a href={paper.paper_url} target="_blank" rel="noreferrer" className="block">
            {paper.title}
          </a>
        </h2>

        <div className="text-sm text-slate-500" title={authors.tooltip}>
          {authors.text}
        </div>
      </header>

      <SummaryBlock summary={paper.summary} abstract={paper.abstract} />

      <footer className="mt-2 flex items-center justify-between border-t border-slate-50 pt-4">
        <div className="flex items-center gap-4">
          <a
            href={arxivPdfUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 transition hover:text-red-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            PDF
          </a>
          <a
            href={arxivAbsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 transition hover:text-accent"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            ArXiv
          </a>
        </div>
        <div className="text-[10px] text-slate-300">{formatDate(paper.collected_at)} 抓取</div>
      </footer>
    </article>
  );
}